import React, { useState } from 'react'
import BackArrowIcon from 'assets/svg/BackArrow'
import Button from 'components/atoms/Button'
import { Drawers } from 'store/drawer/types'
import wrapper from 'components/organisms/Drawers/wrapper'
import { useAppSelector } from 'hooks/redux'
import {
  Drawer as DrawerStyled,
  Container,
  Header,
  BackButton,
  Title,
  Content,
  InfoSection,
  InfoItem,
  InfoLabel,
  InfoValue,
  MessageSection,
  MessageText,
  ButtonSection,
  ActionButton
} from './styles'
import { CustomModal } from 'components'
import { AuthorizationStatus, CancelationStatus, RecurrenceStatus } from 'store/debin/types'

interface DebinDetailDrawerData {
  debinData: {
    id: number,
    externalId: string
    company: string
    sellerTaxId: string
    date: string
    amount: number
    installments: number
    concept: string
    status: RecurrenceStatus
    authorizationStatus: AuthorizationStatus
    cancellationStatus: CancelationStatus
    validForRefund: boolean
    paidQuotes: number
    hasQuoteInProgress: boolean
  }
  onRequestCancel?: (id: number) => void
}

interface DrawerDebinDetailProps {
  showDrawer: boolean
  onClose: () => void
}

const DrawerDebinDetail: React.FC<DrawerDebinDetailProps> = ({
  showDrawer,
  onClose
}) => {
  const [showModalConfirmation, setShowModalConfirmation] = useState(false)
  const { data } = useAppSelector(state => state.drawer)
  const { isLoading } = useAppSelector(state => state.debin)
  const drawerData = data[Drawers.DEBIN_DETAIL] as DebinDetailDrawerData | undefined
  const debinData = drawerData?.debinData

  if (!debinData) return null

  const activeRecurrence =
    debinData.status === RecurrenceStatus.ACTIVE &&
    debinData.authorizationStatus === AuthorizationStatus.AUTHORIZED

  const rejectedRecurrence =
    debinData.status === RecurrenceStatus.INACTIVE &&
    debinData.authorizationStatus === AuthorizationStatus.REJECTED

  const cancelationInProgress =
    debinData.cancellationStatus === CancelationStatus.CANCELATION_IN_PROGRESS ||
    debinData.cancellationStatus === CancelationStatus.REFUND_IN_PROGRESS

  const getMessage = () => {
    if (debinData.hasQuoteInProgress) {
      return 'No es posible realizar ninguna acción debido a que un pago se encuentra PENDIENTE'
    }
    if (cancelationInProgress) {
      return 'El DEBIN está en proceso de cancelación'
    } else {
      if (activeRecurrence) {
        return 'Estás pagando las cuotas de este DEBIN. Podés cancelarlo en cualquier momento.'
      }
      if (rejectedRecurrence) {
        return 'El DEBIN ha sido rechazado.'
      }
    }
    return ''
  }

  const handleOpenCancelModal = () => {
    setShowModalConfirmation(true)
  }

  const onRequestCancel = drawerData?.onRequestCancel

  const handleCancelDebin = () => {
    setShowModalConfirmation(false)
    onClose()

    onRequestCancel?.(debinData.id)
  }

  return (
    <DrawerStyled open={showDrawer} onClose={onClose} direction="right">
      <Container>
        <Header>
          <BackButton type="button" onClick={onClose}>
            <BackArrowIcon />
          </BackButton>
          <Title>Detalle de DEBIN</Title>
        </Header>

        <Content>
          <InfoSection>
            <InfoItem>
              <InfoLabel>Vendedor</InfoLabel>
              <InfoValue>{debinData.sellerTaxId}</InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Prestación</InfoLabel>
              <InfoValue>{debinData.company}</InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Fecha</InfoLabel>
              <InfoValue>{debinData.date}</InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Monto total de la operación</InfoLabel>
              <InfoValue>
                $
                {debinData.amount.toLocaleString('es-AR')}
              </InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Cuotas totales</InfoLabel>
              <InfoValue>{debinData.installments}</InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Cuotas pagas</InfoLabel>
              <InfoValue>{debinData.paidQuotes}</InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Concepto</InfoLabel>
              <InfoValue>{debinData.concept}</InfoValue>
            </InfoItem>
          </InfoSection>

          <MessageSection>
            {debinData.installments !== debinData.paidQuotes && (<MessageText>{getMessage()}</MessageText>)}
            <ButtonSection>
              {activeRecurrence && !cancelationInProgress && debinData.installments !== debinData.paidQuotes
                ? (
                  <ActionButton disabled={debinData.hasQuoteInProgress} onClick={handleOpenCancelModal}>
                    {debinData.validForRefund ? 'Pedir reembolso' : 'Cancelar DEBIN'}
                  </ActionButton>
                  )
                : (
                  <ActionButton onClick={onClose}>
                    Cerrar
                  </ActionButton>
                  )}
            </ButtonSection>
          </MessageSection>
        </Content>
      </Container>

      <CustomModal
        title='Cancelar DEBIN'
        isOpen={showModalConfirmation}
        onClose={() => setShowModalConfirmation(false)}
      >
        <div style={{ textAlign: 'center', padding: '0 20px', marginBottom: '30px' }}>
          <p style={{ marginBottom: '10px' }}>
            {`¿Estás seguro que querés ${
              debinData.validForRefund
                ? 'solicitar el reembolso de'
                : 'cancelar'
            } este DEBIN?`}
          </p>
          <p>Esta acción no se puede deshacer.</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems: 'center' }}>
          <Button
            onPress={handleCancelDebin}
            customStyle={{ width: '80%', marginBottom: '10px' }}
            loading={isLoading}
          >
            Confirmar
          </Button>
          <Button
            onPress={() => setShowModalConfirmation(false)}
            secondary
            customStyle={{ width: '80%' }}
            disabled={isLoading}
          >
            Volver
          </Button>
        </div>
      </CustomModal>
    </DrawerStyled>
  )
}

export default wrapper(DrawerDebinDetail, Drawers.DEBIN_DETAIL)
